import { createPlatformStackNavigator } from "../createPlatformStackNavigator";
import React from "react";
import ScreenHeader, { HeaderButton } from "../../screens/user/ScreenHeader";
import { useI18n } from "../../hooks/i18n-hooks";
import { useCardStyleInterpolator } from "../../hooks/use-card-style-interpolator";
import MissionDetailsScreen from "../../screens/user/MissionDetails";
import MissionTimeSheetScreen from "../../screens/user/MissionTimeSheetScreen";
import MissionsScreen from "../../screens/user/Missions";
import MissionsMultiSignScreen from "../../screens/user/MissionsMultiSign";
import MissionsMultiSignProgressScreen from "../../screens/user/MissionsMultiSignProgress";
import MissionClientFeesScreen from "../../screens/user/MissionClientFees";
import { ListItemProps } from "../../ui_atoms/ListItem";
import MissionInfoDeductionsScreen from "../../screens/user/MissionInfoDeductions";
import PayrollBreakdownListScreen from "../../screens/user/PayrollBreakdownListScreen";
import PayrollBreakdownDetailsScreen from "../../screens/user/PayrollBreakdownDetailsScreen";
import PayrollBreakdownTimeSheetScreen from "../../screens/user/PayrollBreakdownTimeSheetScreen";
import MissionLogsScreen from "../../screens/user/MissionLogs";

export type UserMissionsTabParamList = {
    MissionList: undefined;
    MissionsMultiSign: { missionIds: string[] };
    MissionsMultiSignProgress: { missionIds: string[] };
    MissionDetails: { missionId: string };
    MissionClientFees: { fees: ({ key: string } & ListItemProps)[] };
    MissionInfoDeductions: { deductions: ({ key: string } & ListItemProps)[] };
    MissionTimeSheets: { missionId: string };
    MissionLogs: { missionId: string };
    PayrollBreakdownList: { missionId: string };
    PayrollBreakdownDetails: { payrollBreakdownId: string };
    PayrollBreakdownTimeSheetScreen: { payrollBreakdownId: string };
};

const Stack = createPlatformStackNavigator<UserMissionsTabParamList>();

const UserMissionsNavigation = () => {
    const i18n = useI18n();

    const cardStyleInterpolator = useCardStyleInterpolator();

    return (
        // Every screen sets `headerShown: false` or a custom `header`, so the default header styling
        // is dead code and dropped. Native uses its default push animation; web keeps the JS-stack
        // card interpolator (no animation on web + dim backdrop).
        <Stack.Navigator
            screenOptions={{
                gestureEnabled: true,
            }}
            webScreenOptions={{ cardStyleInterpolator }}
            initialRouteName="MissionList"
        >
            <Stack.Screen
                name="MissionList"
                component={MissionsScreen}
                options={{
                    title: i18n.t("screens.client_profile.tabs.missions.title"),
                    header: () => (
                        <ScreenHeader
                            label={i18n.t("screens.user.tabs.missions")}
                            useHeaderButton
                        />
                    ),
                }}
            />
            <Stack.Screen
                name="MissionsMultiSign"
                component={MissionsMultiSignScreen}
                options={({ navigation, route }) => {
                    // Same key as the sign button, so the header title and the button always
                    // agree on the wording and the count.
                    const title = i18n.t("screens.user.missions.sign_selected", {
                        count: route.params.missionIds.length,
                    });

                    return {
                        title,
                        gestureEnabled: true,
                        header: () => (
                            <ScreenHeader
                                useSmallLabel
                                label={title}
                                leftComponent={
                                    <HeaderButton
                                        label={i18n.t("back")}
                                        onPress={navigation.goBack}
                                    />
                                }
                            />
                        ),
                    };
                }}
            />
            <Stack.Screen
                name="MissionsMultiSignProgress"
                component={MissionsMultiSignProgressScreen}
                options={{
                    // Presented as a modal with no header: the screen closes itself through its
                    // own "Close" button, and going back mid-signature would make no sense.
                    presentation: "modal",
                    headerShown: false,
                    gestureEnabled: false,
                    title: i18n.t("screens.user.missions.multi_sign_progress_title"),
                }}
            />
            <Stack.Screen
                name="MissionDetails"
                component={MissionDetailsScreen}
                options={{
                    headerShown: false,
                    title: i18n.t("mission.nav_title"),
                    gestureEnabled: true,
                }}
            />
            <Stack.Screen
                name="MissionClientFees"
                component={MissionClientFeesScreen}
                options={{
                    header: ({ navigation }) => (
                        <ScreenHeader
                            useSmallLabel
                            onLeftAction={() => navigation.goBack()}
                            label={i18n.t("mission.details.client_fees")}
                        />
                    ),
                    gestureEnabled: true,
                }}
            />
            <Stack.Screen
                name="MissionInfoDeductions"
                component={MissionInfoDeductionsScreen}
                options={{
                    header: ({ navigation }) => (
                        <ScreenHeader
                            useSmallLabel
                            onLeftAction={() => navigation.goBack()}
                            label={i18n.t("mission.details.mission_deductions")}
                        />
                    ),
                    gestureEnabled: true,
                }}
            />
            <Stack.Screen
                name="MissionTimeSheets"
                component={MissionTimeSheetScreen}
                options={{
                    headerShown: false,
                    title: i18n.t("timesheet_list.title"),
                    gestureEnabled: true,
                }}
            />
            <Stack.Screen
                name="PayrollBreakdownList"
                component={PayrollBreakdownListScreen}
                options={{
                    headerShown: false,
                    gestureEnabled: true,
                    title: i18n.t("screens.payroll_breakdown_list.title"),
                }}
            />
            <Stack.Screen
                name="PayrollBreakdownDetails"
                component={PayrollBreakdownDetailsScreen}
                options={{
                    headerShown: false,
                    title: i18n.t("screens.payroll_breakdown.title"),
                    gestureEnabled: true,
                }}
            />
            <Stack.Screen
                name="PayrollBreakdownTimeSheetScreen"
                component={PayrollBreakdownTimeSheetScreen}
                options={{
                    headerShown: false,
                    title: i18n.t("screens.payroll_breakdown.timesheets"),
                    gestureEnabled: true,
                }}
            />
            <Stack.Screen
                name="MissionLogs"
                component={MissionLogsScreen}
                options={{
                    gestureEnabled: true,
                    header: ({ navigation }) => (
                        <ScreenHeader
                            useSmallLabel
                            onLeftAction={navigation.goBack}
                            label={i18n.t("mission.history.title")}
                        />
                    ),
                    title: i18n.t("mission.history.title"),
                }}
            />
        </Stack.Navigator>
    );
};

export default UserMissionsNavigation;
